import { useEffect, useState } from 'react';

/**
 * Retorna `true` enquanto a media query casar. Usado para alternar layouts
 * por largura de tela em JS (em vez de renderizar as duas árvores via CSS
 * `hidden`), evitando montar componentes pesados que não vão aparecer.
 *
 * Caso de uso principal (ficha do paciente / mestre-detalhe): só o desktop
 * (≥1280px = breakpoint `xl` do Tailwind) recebe o layout lista+detalhe; em
 * telas menores o feed atual continua idêntico — ver `useIsDesktop`.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    // Sincroniza caso a query tenha mudado entre o estado inicial e o efeito.
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Atalho: verdadeiro em telas ≥1280px (breakpoint `xl` do Tailwind). */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1280px)');
}
